import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { Card, Row, Col } from 'react-bootstrap';
import { Button } from '@components';
import { toast } from 'react-toastify';

import {
  congViecKhacGetByNhiemVuAction,
  addCongViecKhacAction,
  editCongViecKhacAction,
  deleteCongViecKhacAction,
  filterListCongViecKhac,
} from '@store/reducers/congvieckhac';
import { nhiemVuKhacGetItemAction } from '@store/reducers/nhiemvukhac';
import { nhanVienGetAllAction } from '@store/reducers/nhanVien';
import { TRANG_THAI_CONG_VIEC_KHAC_OPTIONS } from '@app/store/constants';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const toInputDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const toDisplayDate = (dateStr) => {
  if (!dateStr) return '';

  if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [y, m, d] = dateStr.split('-');
    return `${d}/${m}/${y}`;
  }

  const dt = new Date(dateStr);
  if (Number.isNaN(dt.getTime())) return '';

  const d = String(dt.getDate()).padStart(2, '0');
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const y = dt.getFullYear();
  return `${d}/${m}/${y}`;
};

const DEADLINE_SOON_DAYS = 2;
const MS_PER_HOUR = 60 * 60 * 1000;
const MS_PER_DAY = 24 * MS_PER_HOUR;

const isDateOnly = (s) => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s);

const clampPercent = (n) => {
  const v = typeof n === 'number' && !Number.isNaN(n) ? n : 0;
  return Math.max(0, Math.min(100, v));
};

const toStatusKey = (valueOrKey) => {
  const opt = TRANG_THAI_CONG_VIEC_KHAC_OPTIONS.find(
    (o) => o.value === valueOrKey || o.key === valueOrKey
  );
  return opt ? opt.key : 'chuabatdau';
};

const parseDueAsEndOfDay = (raw) => {
  if (!raw) return null;

  if (isDateOnly(raw)) {
    const [y, m, d] = raw.split('-').map((n) => parseInt(n, 10));
    return new Date(y, m - 1, d, 23, 59, 59, 999);
  }

  const dt = new Date(raw);
  if (Number.isNaN(dt.getTime())) return null;

  if (dt.getHours() === 0 && dt.getMinutes() === 0 && dt.getSeconds() === 0) {
    return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate(), 23, 59, 59, 999);
  }

  return dt;
};

const formatDeltaText = (diffMs, isOverdue) => {
  const abs = Math.abs(diffMs);

  if (abs < MS_PER_DAY) {
    const hours = Math.max(1, Math.ceil(abs / MS_PER_HOUR));
    return isOverdue ? `Trễ ${hours} giờ` : `Còn ${hours} giờ`;
  }

  const days = Math.max(1, Math.ceil(abs / MS_PER_DAY));
  return isOverdue ? `Trễ ${days} ngày` : `Còn ${days} ngày`;
};

const getDeadlineWarning = (task) => {
  const statusKey = toStatusKey(task?.status || task?.trangThai);
  if (statusKey === 'hoanthanh') return null;

  const rawDue = task?.ngayDuKienHoanThanh;
  const due = parseDueAsEndOfDay(rawDue);
  if (!due) return null;

  const now = new Date();
  const diffMs = due.getTime() - now.getTime();

  if (diffMs < 0) {
    return {
      type: 'overdue',
      badgeText: `Quá hạn • ${formatDeltaText(diffMs, true)}`,
      tooltip: `Dự kiến: ${toDisplayDate(rawDue)} (đã quá hạn)`,
      cardStyle: { borderLeft: '4px solid #dc3545' },
      badgeStyle: {
        marginLeft: 6,
        padding: '2px 8px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
        background: '#f8d7da',
        color: '#721c24',
        border: '1px solid #f5c6cb',
        whiteSpace: 'nowrap',
      },
    };
  }

  if (diffMs <= DEADLINE_SOON_DAYS * MS_PER_DAY) {
    return {
      type: 'soon',
      badgeText: `Sắp hết hạn • ${formatDeltaText(diffMs, false)}`,
      tooltip: `Dự kiến: ${toDisplayDate(rawDue)} (còn ≤ ${DEADLINE_SOON_DAYS} ngày)`,
      cardStyle: { borderLeft: '4px solid #ffc107' },
      badgeStyle: {
        marginLeft: 6,
        padding: '2px 8px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
        background: '#fff3cd',
        color: '#856404',
        border: '1px solid #ffeeba',
        whiteSpace: 'nowrap',
      },
    };
  }

  return null;
};

// Quy tắc: nếu status=hoanthanh thì HIỂN THỊ 100%
const getEffectiveTienDo = (task) => {
  const statusKey = toStatusKey(task?.status || task?.trangThai);
  if (statusKey === 'hoanthanh') return 100;
  return clampPercent(typeof task?.tienDo === 'number' ? task.tienDo : Number(task?.tienDo) || 0);
};

const extractErrorMessage = (err) => {
  if (!err) return '';
  if (typeof err === 'string') return err;
  if (err.message) return err.message;
  if (err.title) return err.title;
  if (err.errors) {
    try {
      return Object.values(err.errors).flat().join('; ');
    } catch (_) {
      return '';
    }
  }
  return '';
};

const COLUMN_STYLE_MAP = {
  chuabatdau: {
    headerBg: '#e1e5f2',
    headerText: '#333333',
    border: '#c4ccdd',
    bodyBg: '#f7f9fc',
  },
  dangthuchien: {
    headerBg: '#d3e5ff',
    headerText: '#245b8f',
    border: '#aac6ff',
    bodyBg: '#f3f7ff',
  },
  choduyet: {
    headerBg: '#ffe0b2',
    headerText: '#8f6a24',
    border: '#ffc875',
    bodyBg: '#fff5e0',
  },
  hoanthanh: {
    headerBg: '#cef4e4',
    headerText: '#247a55',
    border: '#a0e0c6',
    bodyBg: '#f1fbf6',
  },
};

const TaskForm = ({ mode, initialTask, statusKeyForEdit, onSave, onCancel, submitting }) => {
  const [tenCongViec, setTenCongViec] = useState('');
  const [moTa, setMoTa] = useState('');
  const [tienDo, setTienDo] = useState(0);
  const [ngayBatDau, setNgayBatDau] = useState('');
  const [ngayDuKienHoanThanh, setNgayDuKienHoanThanh] = useState('');

  const isDone = statusKeyForEdit === 'hoanthanh';

  useEffect(() => {
    if (mode === 'edit' && initialTask) {
      setTenCongViec(initialTask.tenCongViec || '');
      setMoTa(initialTask.moTa || '');

      // Nếu đang ở Hoàn thành thì UI hiển thị 100%
      const rawTienDo = Number.isFinite(initialTask.tienDo)
        ? initialTask.tienDo
        : Number(initialTask.tienDo) || 0;

      setTienDo(isDone ? 100 : clampPercent(rawTienDo));
      setNgayBatDau(toInputDate(initialTask.ngayBatDau));
      setNgayDuKienHoanThanh(toInputDate(initialTask.ngayDuKienHoanThanh));
    } else {
      setTenCongViec('');
      setMoTa('');
      setTienDo(0);
      setNgayBatDau('');
      setNgayDuKienHoanThanh('');
    }
  }, [initialTask, mode, isDone]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!tenCongViec.trim()) {
      toast.error('Vui lòng nhập tên công việc');
      return;
    }

    const parsed = parseInt(String(tienDo ?? 0), 10);
    const safeTienDo = isDone ? 100 : clampPercent(Number.isNaN(parsed) ? 0 : parsed);

    const payload = {
      tenCongViec: tenCongViec.trim(),
      moTa: moTa ? moTa.trim() : null,
      tienDo: safeTienDo,
      ngayBatDau: ngayBatDau ? new Date(ngayBatDau).toISOString() : null,
      ngayDuKienHoanThanh: ngayDuKienHoanThanh ? new Date(ngayDuKienHoanThanh).toISOString() : null,
    };

    onSave(payload);
  };

  return (
    <div className="card mb-2 shadow-sm">
      <div className="card-body p-2">
        <h5 className="card-title mb-2" style={{ fontSize: 14 }}>
          {mode === 'edit' ? 'Cập nhật công việc' : 'Thêm công việc mới'}
        </h5>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Tên công việc..."
              value={tenCongViec}
              onChange={(e) => setTenCongViec(e.target.value)}
            />
          </div>

          <div className="form-group">
            <textarea
              className="form-control form-control-sm"
              rows={3}
              placeholder="Mô tả..."
              value={moTa || ''}
              onChange={(e) => setMoTa(e.target.value)}
            />
          </div>

          <div className="form-group mb-2">
            <label className="mb-1" style={{ fontSize: 12, fontWeight: 700 }}>
              Ngày bắt đầu
            </label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={ngayBatDau || ''}
              onChange={(e) => setNgayBatDau(e.target.value || '')}
            />
          </div>

          <div className="form-group mb-2">
            <label className="mb-1" style={{ fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>
              Tiến độ (%)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              className="form-control form-control-sm"
              value={tienDo}
              disabled={isDone}
              onChange={(e) => {
                const v = parseInt(e.target.value || '0', 10);
                const c = Number.isNaN(v) ? 0 : clampPercent(v);
                setTienDo(c);
              }}
            />
            {isDone && (
              <small className="text-muted" style={{ fontSize: 11 }}>
                Trạng thái “Hoàn thành” được tính 100%.
              </small>
            )}
          </div>

          <div className="form-group mb-2">
            <label className="mb-1" style={{ fontSize: 12, fontWeight: 700 }}>
              Ngày dự kiến hoàn thành
            </label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={ngayDuKienHoanThanh || ''}
              onChange={(e) => setNgayDuKienHoanThanh(e.target.value || '')}
            />
          </div>

          <div className="d-flex justify-content-between">
            <Button type="submit" theme="primary" size="sm" disabled={submitting} isLoading={submitting}>
              {mode === 'edit' ? 'Lưu' : 'Thêm'}
            </Button>
            <Button type="button" theme="secondary" size="sm" onClick={onCancel} disabled={submitting}>
              Hủy
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const TaskCard = ({ task, onEdit, onDelete, onDragStart, onDragEnd }) => {
  const startStr = toDisplayDate(task.ngayBatDau);
  const endStr = toDisplayDate(task.ngayDuKienHoanThanh);
  const deadlineWarn = getDeadlineWarning(task);

  return (
    <div
      className="card mb-2 shadow-sm"
      draggable
      onDragStart={(e) => onDragStart(e, task.id)}
      onDragEnd={onDragEnd}
      style={deadlineWarn?.cardStyle}
    >
      <div className="card-body p-2">
        <h6 className="mb-1">{task.tenCongViec}</h6>

        {task.moTa && (
          <p className="mb-1 text-muted" style={{ fontSize: 12 }}>
            {task.moTa}
          </p>
        )}

        <div className="mb-1" style={{ fontSize: 12 }}>
            {(startStr || endStr) && (
                <div
                style={{
                    marginTop: 4,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 10,
                }}
                >
                {startStr && <div>Bắt đầu: {startStr}</div>}
                {endStr && <div>Dự kiến: {endStr}</div>}
                </div>
            )}

            {deadlineWarn && (
                <div style={{ marginTop: 6 }}>
                <span title={deadlineWarn.tooltip} style={deadlineWarn.badgeStyle}>
                    {deadlineWarn.badgeText}
                </span>
                </div>
            )}
        </div>

        <div className="mb-1" style={{ fontSize: 12, color: '#333' }}>
          Tiến độ: <strong>{getEffectiveTienDo(task)}%</strong>
        </div>

        <div className="d-flex justify-content-end">
          <button type="button" className="btn btn-xs btn-link text-primary mr-2" onClick={onEdit}>
            Sửa
          </button>
          <button type="button" className="btn btn-xs btn-link text-danger" onClick={onDelete}>
            Xoá
          </button>
        </div>
      </div>
    </div>
  );
};

const CongViecKhac = () => {
  const query = useQuery();
  const nhiemVuId = query.get('nhiemVuId');

  const dispatch = useDispatch();
  const history = useHistory();

  const list = useSelector((state) => state.congViecKhac.listCongViecKhac || []);
  const nhiemVuItem = useSelector((state) => state.nhiemVuKhac.nhiemVuKhacItem);
  useSelector((state) => state.nhanVien.list || []); 

  const [formMode, setFormMode] = useState(null); 
  const [formTask, setFormTask] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [draggingTaskId, setDraggingTaskId] = useState(null);

  const originalTienDoRef = useRef({}); 

  useEffect(() => {
    if (nhiemVuId) {
      dispatch(congViecKhacGetByNhiemVuAction(nhiemVuId));
      dispatch(nhiemVuKhacGetItemAction(nhiemVuId));
    }
    dispatch(nhanVienGetAllAction());
  }, [dispatch, nhiemVuId]);

  const tasksByStatus = useMemo(() => {
    const grouped = { chuabatdau: [], dangthuchien: [], choduyet: [], hoanthanh: [] };
    (list || []).forEach((t) => {
      const key = toStatusKey(t.status || t.trangThai);
      if (grouped[key]) grouped[key].push(t);
    });
    return grouped;
  }, [list]);

  // Tiến độ tổng: dùng effective (Hoàn thành tính 100)
  const progressPercent = useMemo(() => {
    const total = (list || []).length;
    if (total === 0) return 0;
    const sum = (list || []).reduce((acc, t) => acc + getEffectiveTienDo(t), 0);
    return Math.round(sum / total);
  }, [list]);

  const openCreate = () => {
    setFormMode('create');
    setFormTask(null);
  };

  const openEdit = (task) => {
    setFormMode('edit');
    setFormTask(task);
  };

  const closeForm = () => {
    setFormMode(null);
    setFormTask(null);
  };

  const handleSave = async (values) => {
    if (!nhiemVuId) return toast.error('Thiếu thông tin nhiệm vụ');

    try {
      setSubmitting(true);

      if (formMode === 'create') {
        const payload = {
          nhiemVuId: parseInt(nhiemVuId, 10),
          tenCongViec: values.tenCongViec,
          moTa: values.moTa,
          tienDo: values.tienDo ?? 0,
          status: 'chuabatdau',
          ngayBatDau: values.ngayBatDau || null,
          ngayDuKienHoanThanh: values.ngayDuKienHoanThanh || null,
        };

        await dispatch(addCongViecKhacAction(payload)).unwrap();
        toast.success('Tạo công việc thành công');
      } else if (formMode === 'edit' && formTask) {
        const currentStatusKey = toStatusKey(formTask.status || formTask.trangThai);

        // Nếu đang “hoanthanh” thì giữ 100 khi lưu, nhưng vẫn cho phép kéo lại để về % cũ
        if (currentStatusKey === 'hoanthanh') {
          // đảm bảo nếu chưa có “gốc” thì lấy từ DB hiện tại
          if (originalTienDoRef.current[formTask.id] === undefined) {
            const old = clampPercent(Number(formTask.tienDo) || 0);
            originalTienDoRef.current[formTask.id] = old;
          }
        }

        const payload = {
          nhiemVuId: formTask.nhiemVuId || parseInt(nhiemVuId, 10),
          tenCongViec: values.tenCongViec,
          moTa: values.moTa,
          tienDo: currentStatusKey === 'hoanthanh' ? 100 : (values.tienDo ?? (formTask.tienDo ?? 0)),
          status: formTask.status || formTask.trangThai || 'chuabatdau',
          ngayBatDau: values.ngayBatDau || null,
          ngayDuKienHoanThanh: values.ngayDuKienHoanThanh || null,
        };

        await dispatch(editCongViecKhacAction({ id: formTask.id, data: payload })).unwrap();
        toast.success('Cập nhật công việc thành công');
      }

      await dispatch(congViecKhacGetByNhiemVuAction(nhiemVuId));
      closeForm();
    } catch (err) {
      const msg = extractErrorMessage(err);
      toast.error(msg ? `Thao tác thất bại: ${msg}` : 'Thao tác thất bại');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (task) => {
    if (!window.confirm('Bạn có chắc muốn xoá công việc này?')) return;
    try {
      await dispatch(deleteCongViecKhacAction(task.id)).unwrap();
      dispatch(filterListCongViecKhac(task.id));
      toast.success('Xoá công việc thành công');
    } catch (err) {
      const msg = extractErrorMessage(err);
      toast.error(msg ? `Xoá thất bại: ${msg}` : 'Xoá thất bại');
    }
  };

  const handleDragStart = (e, taskId) => {
    try {
      e.dataTransfer.setData('text/plain', String(taskId));
    } catch (_) {}
    setDraggingTaskId(taskId);
  };

  const handleDragEnd = () => {
    setDraggingTaskId(null);
  };

  const handleDropOnStatus = async (statusKey) => {
    if (!draggingTaskId) return;

    const taskId =
      typeof draggingTaskId === 'string' ? parseInt(draggingTaskId, 10) : draggingTaskId;

    const task = (list || []).find((t) => t.id === taskId || String(t.id) === String(taskId));
    if (!task) return;

    const oldStatusKey = toStatusKey(task.status || task.trangThai);
    if (oldStatusKey === statusKey) {
      setDraggingTaskId(null);
      return;
    }

    let tienDoToSend = clampPercent(Number(task.tienDo) || 0);

    if (statusKey === 'hoanthanh' && oldStatusKey !== 'hoanthanh') {
      if (originalTienDoRef.current[task.id] === undefined) {
        originalTienDoRef.current[task.id] = tienDoToSend;
      }
      tienDoToSend = 100;
    }

    if (oldStatusKey === 'hoanthanh' && statusKey !== 'hoanthanh') {
      if (originalTienDoRef.current[task.id] === undefined) {
        originalTienDoRef.current[task.id] = clampPercent(Number(task.tienDo) || 0);
      }
      const saved = originalTienDoRef.current[task.id];
      if (Number.isFinite(saved)) {
        tienDoToSend = saved;
      }
    }

    try {
      const updateModel = {
        nhiemVuId: task.nhiemVuId || parseInt(nhiemVuId, 10),
        tenCongViec: task.tenCongViec,
        moTa: task.moTa,
        tienDo: tienDoToSend,
        status: statusKey,
        ngayBatDau: task.ngayBatDau || null,
        ngayDuKienHoanThanh: task.ngayDuKienHoanThanh || null,
      };

      await dispatch(editCongViecKhacAction({ id: task.id, data: updateModel })).unwrap();
      await dispatch(congViecKhacGetByNhiemVuAction(nhiemVuId));

      // Nếu kéo ra khỏi “hoanthanh” thì xoá cache để lần sau lưu lại đúng
      if (oldStatusKey === 'hoanthanh' && statusKey !== 'hoanthanh') {
        delete originalTienDoRef.current[task.id];
      }
    } catch (err) {
      const msg = extractErrorMessage(err);
      toast.error(msg ? `Cập nhật trạng thái thất bại: ${msg}` : 'Cập nhật trạng thái thất bại');
    } finally {
      setDraggingTaskId(null);
    }
  };

  const renderColumn = (statusKey) => {
    const opt = TRANG_THAI_CONG_VIEC_KHAC_OPTIONS.find((o) => o.key === statusKey);
    const label = opt ? opt.label : statusKey;
    const listFor = tasksByStatus[statusKey] || [];
    const styleCfg = COLUMN_STYLE_MAP[statusKey] || COLUMN_STYLE_MAP.chuabatdau;

    const deadlineCounts = listFor.reduce(
      (acc, t) => {
        const w = getDeadlineWarning(t);
        if (!w) return acc;
        if (w.type === 'overdue') acc.overdue += 1;
        if (w.type === 'soon') acc.soon += 1;
        return acc;
      },
      { overdue: 0, soon: 0 }
    );

    return (
      <Col
        key={statusKey}
        md={3}
        onDragOver={(e) => e.preventDefault()}
        onDrop={() => handleDropOnStatus(statusKey)}
      >
        <div
          className="card mb-3 shadow-sm"
          style={{
            borderColor: styleCfg.border,
            borderWidth: 1,
            borderStyle: 'solid',
            backgroundColor: '#ffffff',
          }}
        >
          <div
            className="card-header d-flex align-items-center"
            style={{
              backgroundColor: styleCfg.headerBg,
              borderBottomColor: styleCfg.border,
              borderBottomWidth: 1,
              borderBottomStyle: 'solid',
              color: styleCfg.headerText,
              fontWeight: 600,
              fontSize: 14,
              height: 52,
              paddingTop: 8,
              paddingBottom: 8,
            }}
          >
            <div className="d-flex align-items-center" style={{ gap: 6, whiteSpace: 'nowrap' }}>
              <span>{label}</span>

              {deadlineCounts.overdue > 0 && (
                <span
                  title="Số công việc quá hạn (chưa hoàn thành)"
                  style={{
                    padding: '2px 6px',
                    borderRadius: 999,
                    fontSize: 11,
                    fontWeight: 700,
                    background: '#f8d7da',
                    color: '#721c24',
                    border: '1px solid #f5c6cb',
                  }}
                >
                  Quá hạn: {deadlineCounts.overdue}
                </span>
              )}

              {deadlineCounts.soon > 0 && (
                <span
                  title={`Số công việc sắp hết hạn (≤ ${DEADLINE_SOON_DAYS} ngày, chưa hoàn thành)`}
                  style={{
                    padding: '2px 6px',
                    borderRadius: 999,
                    fontSize: 11,
                    fontWeight: 700,
                    background: '#fff3cd',
                    color: '#856404',
                    border: '1px solid #ffeeba',
                  }}
                >
                  Sắp hết hạn: {deadlineCounts.soon}
                </span>
              )}
            </div>

            <div style={{ flex: 1, textAlign: 'center', fontWeight: 500 }}>{listFor.length}</div>

            {statusKey === 'chuabatdau' && (
              <button
                type="button"
                className="btn btn-outline-secondary d-flex align-items-center justify-content-center p-0"
                onClick={openCreate}
                title="Thêm công việc mới"
                style={{ width: 32, height: 32, borderRadius: 6 }}
              >
                <span style={{ fontSize: 18, lineHeight: 1, position: 'relative', top: -1 }}>+</span>
              </button>
            )}
          </div>

          <div className="card-body" style={{ minHeight: 260, backgroundColor: styleCfg.bodyBg }}>
            {statusKey === 'chuabatdau' && formMode && (
              <TaskForm
                mode={formMode}
                initialTask={formTask}
                statusKeyForEdit={formTask ? toStatusKey(formTask.status || formTask.trangThai) : 'chuabatdau'}
                onSave={handleSave}
                onCancel={closeForm}
                submitting={submitting}
              />
            )}

            {listFor.map((t) => (
              <TaskCard
                key={t.id}
                task={t}
                onEdit={() => openEdit(t)}
                onDelete={() => handleDelete(t)}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              />
            ))}
          </div>
        </div>
      </Col>
    );
  };

  return (
    <>
      <Button theme="secondary" onClick={() => history.goBack()} className="mb-3">
        ← Quay lại
      </Button>

      <Card className="mb-3">
        <Card.Header as="h5">{nhiemVuItem?.ten || nhiemVuItem?.title || 'Công việc khác'}</Card.Header>
        <Card.Body>
          <p className="text-muted mb-3">{nhiemVuItem?.moTa}</p>

          <Row className="align-items-center">
            <Col md={3}>
              <strong>Tiến độ tổng</strong>
              <p className="mb-0">{progressPercent}%</p>
            </Col>
            <Col md={9}>
              <div className="progress" style={{ height: 8, marginTop: 5 }}>
                <div
                  className="progress-bar bg-success"
                  role="progressbar"
                  style={{ width: `${progressPercent}%` }}
                  aria-valuemin="0"
                  aria-valuemax="100"
                />
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <h4 className="mb-3">Bảng công việc - {nhiemVuItem?.ten || ''}</h4>

      <Row>
        {['chuabatdau', 'dangthuchien', 'choduyet', 'hoanthanh'].map((k) => renderColumn(k))}
      </Row>
    </>
  );
};

export default CongViecKhac;
